import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider'; // Import Divider
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import { trackTickerSearch } from '../utils/logActivity';
import { defaultTickerForMarket } from '../utils/branding';
import { CUSTOM_SYMBOL } from '../constants/customSymbol';
import {
  ASSET_CLASSES, DEFAULT_ASSET_CLASS, marketParamFor, defaultTickerForAssetClass,
} from '../constants/assetClasses';



function SymbolList({ onSelectSymbol }) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [symbols, setSymbols] = useState([]);
  const [error, setError] = useState(null);
  const { api, user } = useAuth();
  const { config: appConfig } = useConfig();

  /**
   * The market to load symbols for.
   *
   * The signed-in customer's own market wins. appConfig.market is baked into the bundle at build
   * time for the whole deployment, so using it alone meant every user was shown the US list no
   * matter what their profile said — which is why changing a user's market in the admin console
   * appeared to do nothing. It remains the fallback for anonymous visitors and for the moment
   * before the profile has loaded.
   *
   * The backend is authoritative regardless: it serves a signed-in user their own market and
   * ignores this parameter. Sending the right value keeps the request and the response in
   * agreement rather than relying on the server to correct it.
   */
  const userMarketId = user?.marketId;
  const ownMarket =
    userMarketId === 2 ? 'IN'
      : userMarketId === 1 ? 'US'
        : (appConfig?.market === 'IN' ? 'IN' : 'US');

  /**
   * Which asset class the picker is showing: S (stocks), C (commodities) or P (pairs).
   *
   * <p>Stocks by default, because that is what this product has always been and a customer who
   * has never pressed anything must see what they saw yesterday.
   */
  const [assetClass, setAssetClass] = useState(DEFAULT_ASSET_CLASS);

  /**
   * The {@code market=} value sent to the API.
   *
   * <p>For S this is the customer's OWN market, which is the whole of the existing behaviour —
   * the backend serves a signed-in user their own equities and ignores the parameter, so sending
   * the right value keeps the request and the response in agreement rather than relying on the
   * server to correct it.
   *
   * <p>For C and P it is the single letter, which {@code Market.resolve} accepts directly. Those
   * two are global: the backend deliberately does NOT override them with the customer's market,
   * because gold is not a rival equity universe. Without that exemption a US customer pressing C
   * would be handed the US stock list and the toggle would appear to do nothing.
   */
  const backendMarket = marketParamFor(assetClass, ownMarket);

  // Held in a ref so a parent that recreates this callback each render cannot turn every render
  // into another symbol fetch, while the effect still calls the current one.
  const onSelectRef = useRef(onSelectSymbol);
  useEffect(() => { onSelectRef.current = onSelectSymbol; }, [onSelectSymbol]);

  useEffect(() => {
    const fetchSymbols = () => {
      // Deliberately keyed on backendMarket rather than a one-shot `initialized` flag: the
      // profile arrives after the first render, so a fetch that ran only once would always be
      // the pre-profile one and an India customer would keep the US list they were served
      // before their market was known.
      if (!appConfig) return;

      console.log('[SymbolList] loading symbols for market:', backendMarket,
        '(user marketId:', userMarketId, ', deployment default:', appConfig.market, ')');

      api.get(`/api/symbols?market=${backendMarket}`)
        .then(response => {
          setSymbols(response.data);
          setError(null);
          
          // Open on the symbol that belongs to the market that just loaded: SPY for
          // US/International, NSEI for India. appConfig.defaultTicker is a single deployment-wide
          // value (SPY), so an India customer used to land on whatever happened to be first in
          // their list. It is kept only as the fallback for an unrecognised market.
          if (response.data.length > 0) {
            // THE ASSET CLASS'S OWN DEFAULT WINS, and it has to: defaultTickerForMarket knows
            // only the geographic markets, so for C and P it answers null and the fallback chain
            // would land on SPY — a symbol that is not in either list, so the picker would then
            // silently select whatever happened to be first. Gold for commodities and EUR/USD
            // for pairs are the recognisable members of their own lists.
            const defaultTicker = defaultTickerForAssetClass(assetClass)
              || defaultTickerForMarket(backendMarket)
              || appConfig.defaultTicker
              || 'SPY';
            
            // Find the default ticker in the list and select it
            const defaultSymbol = response.data.find(symbol => symbol === defaultTicker);
            if (defaultSymbol) {
              setSelected(defaultSymbol);
              if (onSelectRef.current) onSelectRef.current(defaultSymbol);
            } else {
              // If default ticker not found, select the first symbol
              const firstSymbol = response.data[0];
              setSelected(firstSymbol);
              if (onSelectRef.current) onSelectRef.current(firstSymbol);
            }
          }
        })
        .catch(() => {
          setSymbols([]);
          setError('Could not load symbols.');
        });
    };

    fetchSymbols();
    // assetClass as well as backendMarket: for an India customer, S sends "IN" and so would a
    // hypothetical class that resolved to the same market, so keying on backendMarket alone
    // could miss a switch. Keying on both means every toggle press refetches exactly once.
  }, [api, appConfig, backendMarket, userMarketId, assetClass]);

  /**
   * The list, IN ALPHABETICAL ORDER.
   *
   * <h3>Why this is not the order the API sends</h3>
   *
   * A ticker list is an append-only file, so every symbol added after the original load sits in an
   * unsorted tail behind the alphabetical block. Rendered in file order that tail is effectively
   * invisible: DJI arrived between SPCX and GSPC near the very bottom, and a customer scrolling to
   * D saw DIS, DLR, DOV and concluded the Dow had never been added. It had — 22 symbols were
   * reachable only by typing their name into the search box, which you cannot do for a symbol you
   * do not know is there.
   *
   * <p>Sorted here rather than in the file, because file order is a storage detail: the updater
   * appends, and a list rewritten to stay sorted would be rewritten on every addition for the sake
   * of a picker. Sorted here rather than in the backend for the same reason in reverse — the API
   * returns the list as stored, and this is the one place that needs it ordered for a human.
   */
  const orderedSymbols = useMemo(
    () => {
      const sorted = [...symbols].sort((a, b) => a.localeCompare(b));
      // THE SYNTHETIC UPLOAD ENTRY, APPENDED rather than sorted.
      //
      // It must sit at the bottom, and relying on "Z" collating last would be luck rather than a
      // rule: `localeCompare` puts punctuation before letters, so `Z-Custom` sorts BEFORE the real
      // ticker `ZYXI` -- which is in the US list. Appending states the intent and survives the next
      // Z-something being added.
      //
      // Filtered first, so a backend that ever served a ticker of this name could not produce two
      // identical rows.
      return [...sorted.filter((s) => s !== CUSTOM_SYMBOL), CUSTOM_SYMBOL];
    },
    [symbols],
  );

  const handleClick = (symbol) => {
    setSelected(symbol);
    if (onSelectSymbol) onSelectSymbol(symbol);

    // Records which symbols customers actually pick, and what they had typed when they
    // picked one. Fire-and-forget: it never blocks the selection.
    const market = backendMarket;
    const matchCount = search
      ? symbols.filter(s => s.toLowerCase().includes(search.toLowerCase())).length
      : null;
    trackTickerSearch(api, symbol, market, matchCount);
  };

  // MUI imports
  // (Place these at the top: import Box from '@mui/material/Box'; import Paper from '@mui/material/Paper'; import TextField from '@mui/material/TextField'; import List from '@mui/material/List'; import ListItem from '@mui/material/ListItem'; import ListItemButton from '@mui/material/ListItemButton'; import ListItemText from '@mui/material/ListItemText'; import Typography from '@mui/material/Typography';)
  return (
    // The sidebar is a PAPER surface, and its heavy border is the slate on light and the theme's
    // own divider on dark -- a 3px #1e293b frame around a #0d1117 panel is invisible, so the
    // column would lose the edge that separates it from the canvas.
    <Box sx={{ width: 112, display: 'flex', flexDirection: 'column', p: 0, m: 0,
      bgcolor: 'background.paper', boxSizing: 'border-box',
      border: '3px solid',
      borderColor: (t) => (t.palette.mode === 'dark' ? 'divider' : '#1e293b') }}>
      {/*
        THE ASSET CLASS TOGGLES — S / C / P, in a row at the top of the list.

        Small circular buttons rather than a dropdown or tabs: the column is 112px wide, which a
        three-item dropdown would fill entirely and tabs would overflow. Three 28px circles fit
        with room to spare and read as a set of mutually exclusive choices, which is what they are.

        The SEARCH BOX sits below them deliberately. A search term is scoped to whichever list is
        showing, so the class is the broader choice and belongs above the narrower one.
      */}
      <Box
        role="group"
        aria-label="Asset class"
        sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, pt: 1, pb: 0.25 }}
      >
        {ASSET_CLASSES.map((entry) => {
          const active = entry.key === assetClass;
          return (
            <Tooltip key={entry.key} title={entry.tooltip} arrow placement="bottom">
              <ButtonBase
                data-testid={`asset-class-${entry.key}`}
                aria-label={entry.name}
                aria-pressed={active}
                onClick={() => setAssetClass(entry.key)}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  fontSize: 13,
                  fontWeight: 700,
                  lineHeight: 1,
                  // The selected one is filled; the others are outlined. Colour alone would not
                  // read on this panel, and a border-only difference is easy to miss at 28px --
                  // so the two channels move together. Through theme tokens rather than the
                  // slate pair they were written in: `#1e293b` fill on a `#0d1117` panel is a
                  // selected pill that looks like an unselected one.
                  color: active ? 'primary.contrastText' : 'text.secondary',
                  bgcolor: active ? 'primary.main' : 'transparent',
                  border: '1.5px solid',
                  borderColor: active ? 'primary.main' : 'divider',
                  transition: 'none',
                  '&:hover': {
                    bgcolor: active ? 'primary.dark' : 'action.hover',
                    borderColor: 'primary.main',
                  },
                }}
              >
                {entry.label}
              </ButtonBase>
            </Tooltip>
          );
        })}
      </Box>

      <TextField
        variant="outlined"
        size="small"
        placeholder="Search Symbol..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        sx={{ m: 1, mb: 0.5, borderRadius: 1, bgcolor: 'background.default' }}
        inputProps={{ style: { fontSize: 16 } }}
      />
      <Typography sx={{ fontSize: '18px', fontWeight: 700, paddingLeft: '16px', paddingRight: (theme) => theme.spacing(1), py: 1, color: 'text.primary', bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider', textDecoration: 'underline' }}>
        Symbol
      </Typography>
      {/* This Box is the main scrollable container for the list */}
      <Box sx={{
          direction: 'rtl', // Move scrollbar to the left for WebKit
          overflowY: 'auto',
          width: '100%',
          maxHeight: 'calc(100vh - 150px)',
          minHeight: 0,
          // Explicit scrollbar styling
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: (theme) => theme.palette.background.default, // Or a light grey
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: (theme) => theme.palette.grey[400], // A visible grey
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.grey[500],
            }
          },
          // For Firefox
          scrollbarWidth: 'thin',
          scrollbarColor: (theme) => `${theme.palette.grey[400]} ${theme.palette.background.default}`,
        }}>

        {symbols.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
            {error || 'No symbols found.'}
          </Typography>
        ) : (
          // Removed redundant inner div with overflowY: 'auto'
          <List dense disablePadding sx={{ width: '100%', direction: 'ltr' }}> {/* Reset direction for list content */}
            {orderedSymbols.filter(symbol => symbol.toLowerCase().includes(search.toLowerCase())).map(symbol => (
              <React.Fragment key={symbol + '-item'}> {/* Added React.Fragment wrapper */}
                <ListItem disablePadding> {/* Removed key={symbol} from ListItem as Fragment now has a key */}
                  <ListItemButton
                    selected={selected === symbol}
                    onClick={() => handleClick(symbol)}
                    onDoubleClick={() => { if (onSelectSymbol) onSelectSymbol(symbol); }}
                    sx={(theme) => ({
                      // borderRadius: 2, // Removing individual item border radius for a more continuous list look with dividers
                      // mb: 0.5, // Removing margin bottom as dividers will provide separation
                      transition: 'background 0.2s, color 0.2s',
                      '&:hover': { 
                        bgcolor: theme.palette.action.hover, 
                      },
                      '&.Mui-selected': {
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText, // This should make text color contrast with primary.main
                        '&:hover': {
                          bgcolor: theme.palette.primary.dark,
                        }
                      },
                      paddingLeft: '16px', // Align text to start at 33% of the width
                      paddingRight: (theme) => theme.spacing(1), // Keep some right padding
                      py: '4px', // Reduced vertical padding to pack items tighter
                    })}
                  >
                    <ListItemText 
                        primary={symbol} 
                        primaryTypographyProps={{ 
                            fontWeight: 500, 
                            fontSize: 16 
                            // Removed hardcoded color: '#0f172a' to allow inheritance from ListItemButton's selected state
                        }} 
                    />
                  </ListItemButton>
                </ListItem>
                <Divider component="li" />
              </React.Fragment> /* Closing React.Fragment wrapper */
            ))}
            </List>
          // Removed redundant inner div
        )}
      </Box>
      {/* Removed redundant bottom bar Box, relying on the main container's border */}
    </Box>
  );
}

SymbolList.propTypes = {
  onSelectSymbol: PropTypes.func,
};

export default SymbolList;
